// Player search: the Add Player modal, and the box above the player list.
//
// Extracted verbatim from js/app.js. Both search boxes are backed by the same
// Web Worker (js/workers/rating-worker.js), which holds the FIDE rating list
// and answers 'search' postMessage requests with a debounced 'results' event.
// For an arbiter, NSF's member register is searched alongside it and answers
// first (see "NSF first" below).
//
// initPlayerSearch(deps) wires everything up; call once from app.js.
//  deps.checkActiveTournament: async () => boolean — guards adding a player
//    from the global search when no tournament is open (defined in app.js).

import { escapeHtml } from '../utils/formatting.js';
import { getPlayerDisplayRating } from './players.js';
import { state, getActiveSettings } from '../state.js';
import { syncPlayers, searchCachedPlayers, getCachedPlayers } from '../services/player-db.js';
import { callFunction, FUNCTIONS_BASE } from '../services/functions-api.js';
import { RATING_WORKER_URL } from '../services/backend.js';
import { NSF_SEARCH_ADMINS_ONLY, rememberNsfPick } from '../services/nsf-membership.js';
import { nsfKFactor } from '../utils/nsf-membership.js';

// FIDE Database DOM Elements
const playerDbSearch = document.getElementById('player-db-search');
const dbSearchResults = document.getElementById('db-search-results');
const searchSpinner = document.getElementById('search-loading-spinner');

const globalPlayerSearch = document.getElementById('global-player-search');
const globalSearchResults = document.getElementById('global-search-results');
const globalSearchSpinner = document.getElementById('global-search-spinner');
const globalSearchSource = document.getElementById('global-search-source');
const globalSearchUseDb = document.getElementById('global-search-use-db');

const addPlayerModal = document.getElementById('add-player-modal');
const addPlayerForm = document.getElementById('add-player-form');
const btnSavePlayer = document.getElementById('btn-save-player');

// Rating Worker Initialization
let ratingWorker = null;
try {
    if (window.Worker) {
        // Root-absolute: a worker URL resolves against the page, and the page is
        // also served at '/t/<id>/<tab>'. See the note in index.html.
        ratingWorker = new Worker(RATING_WORKER_URL);
        ratingWorker.onmessage = (e) => {
            const { action, results, error } = e.data;
            if (action === 'results') {
                fideAnswered(!!e.data.isGlobal, e.data.query, results || [], '');
            } else if (action === 'loading') {
                const target = e.data.isGlobal ? globalSearchResults : dbSearchResults;
                if (target) {
                    target.innerHTML = `<div class="search-result-item loading-text"><i class="ri-loader-4-line spin"></i> ${e.data.status}</div>`;
                    target.classList.remove('hidden');
                }
            } else if (error) {
                console.error('Rating Worker Error:', error);
                fideAnswered(!!e.data.isGlobal, e.data.query, [], error);
            }
        };
        ratingWorker.onerror = (err) => {
            console.error('Worker failed to load:', err);
        };
    }
} catch (e) {
    console.warn('Web Workers are not supported or blocked in this environment (likely a local file). Rating search will not be available.');
}

// Guard set by initPlayerSearch(); async () => boolean.
let checkActiveTournament = async () => true;

// The FIDE list lives on the server now, and a cold Cloud Function instance
// spends several seconds loading it — which the user used to pay on their
// first search. Warm it up as soon as an arbiter signs in, and again on the
// first focus of a search box (covers guests and any missed sign-in event).
let prewarmSent = false;
function prewarmFideSearch() {
    if (prewarmSent || !ratingWorker) return;
    prewarmSent = true;
    ratingWorker.postMessage({ action: 'prewarm' });
}

// Dispatched by js/services/firebase-auth.js on sign-in. A custom event keeps
// firebase-auth.js free of an import into the UI module graph.
document.addEventListener('zw:arbiter-signed-in', prewarmFideSearch);
if (window.__zwArbiterSignedIn) prewarmFideSearch(); // sign-in already fired

// --- NSF first ----------------------------------------------------------------
//
// Outside the arbiter's own database, a search looks in NSF's register of paid
// members as well as in FIDE's rating list, and NSF answers first: most players at
// a Norwegian tournament are members, and many of them — children above all — have
// no FIDE record at all. One list: NSF's members on top, FIDE's players below,
// minus those NSF already showed (the same FIDE ID). A member's ratings and title
// are FIDE's; NSF's register gives none (functions/lib/nsf-check.js searchView).
//
// The register search is for arbiter accounts only, and for now only those that are
// also administrators: NSF_SEARCH_ADMINS_ONLY (functions/lib/nsf-api.js refuses
// everyone else). The membership check is open to every arbiter; this is not, yet.
// Anyone else, and an arbiter while it is down, gets FIDE alone, as before.

// Refused once (401/403): not asked again in this page's life.
let nsfSearchRefused = false;

function nsfSearchAvailable() {
    const auth = window.authApp;
    if (nsfSearchRefused || !auth || typeof auth.getRole !== 'function') return false;
    if (auth.getRole() !== 'organizer') return false;
    if (NSF_SEARCH_ADMINS_ONLY && !(typeof auth.isAdmin === 'function' && auth.isAdmin())) return false;
    return typeof auth.isEmailVerified !== 'function' || auth.isEmailVerified();
}

// NSF's members for a query, or [] when no answer is to be had.
async function searchNsf(query) {
    if (!nsfSearchAvailable()) return [];
    const r = await callFunction('nsfApi', { action: 'search', query });
    if (r.ok) return Array.isArray(r.results) ? r.results : [];
    if (r.status === 401 || r.status === 403) nsfSearchRefused = true;
    return [];
}

// How long one source's answer waits for the other's before it is drawn alone. The
// latecomer is drawn in when it arrives; the wait only spares the list a jump.
const COMBINE_WAIT_MS = 1200;

// The search running in each box, keyed by isGlobal: its query, and each source's
// answer once in (undefined while waiting). A newer search replaces it, and answers
// for the old one are dropped.
const runs = new Map();

function startSearch(query, resultsEl, spinnerEl, isGlobal) {
    const previous = runs.get(isGlobal);
    if (previous) clearTimeout(previous.timer);
    const run = { query, resultsEl, spinnerEl, isGlobal, nsf: undefined, fide: undefined, fideError: '', timer: null, drawn: false };
    runs.set(isGlobal, run);

    searchNsf(query).then(members => settle(run, 'nsf', members));
    if (ratingWorker) ratingWorker.postMessage({ action: 'search', query, isGlobal });
    else settle(run, 'fide', []);
}

// The worker's answer. Its `query` names the search it answers (an older worker
// build sent none, and its answer is taken as the current one's).
function fideAnswered(isGlobal, query, results, error) {
    const run = runs.get(isGlobal);
    if (!run || (query !== undefined && query !== run.query)) return;
    run.fideError = error || '';
    settle(run, 'fide', results);
}

function settle(run, source, results) {
    if (runs.get(run.isGlobal) !== run) return;
    run[source] = results;
    clearTimeout(run.timer);
    const complete = run.nsf !== undefined && run.fide !== undefined;
    if (complete || run.drawn) drawRun(run);
    else run.timer = setTimeout(() => drawRun(run), COMBINE_WAIT_MS);
}

function drawRun(run) {
    if (runs.get(run.isGlobal) !== run) return;
    const complete = run.nsf !== undefined && run.fide !== undefined;
    const members = (run.nsf || []).map(m => nsfMemberToResult(m));
    const fideIds = new Set(members.map(m => m.fideId).filter(Boolean));
    const fide = run.fide || [];
    // FIDE's row for a member it found too fills in their ratings and title.
    const fideById = new Map(fide.filter(p => p.fideId).map(p => [String(p.fideId), p]));
    const nsfItems = members.map(m => (m.fideId && fideById.has(m.fideId)) ? withFideRecord(m, fideById.get(m.fideId)) : m);
    const fideItems = fide.filter(p => !p.fideId || !fideIds.has(String(p.fideId)));
    // The members FIDE's answer left out have their rows fetched now, while the arbiter
    // reads the list, so that picking one does not wait for it (fideRow).
    if (complete) nsfItems.forEach(m => { if (!m.fideLoaded) fideRow(m.fideId); });

    // Nothing yet, and the other answer still coming: keep the spinner, draw nothing.
    if (!complete && nsfItems.length + fideItems.length === 0) return;
    run.drawn = true;
    run.spinnerEl.classList.toggle('hidden', complete);
    // Headings only when NSF has found someone; without, the list looks as it always did.
    const sections = nsfItems.length
        ? [{ heading: tr('NSF members'), items: nsfItems }, { heading: 'FIDE', items: fideItems }]
        : [{ items: fideItems }];
    renderResultList(run.resultsEl, sections, run.isGlobal, run.fideError);
}

// An NSF member in the shape the list and the Add Player form take. `fideLoaded`
// says whether their FIDE row is in: until it is, a member with a FIDE ID shows no
// rating, and picking them waits for it (fideRow). `kFactors` is NSF's K per FIDE
// list; the pick takes the one the group is rated on (nsfKFactor). `membershipYear`
// is the year they have paid for, which their badge shows once they are added
// (rememberNsfPick).
function nsfMemberToResult(m) {
    return {
        source: 'nsf',
        nsfId: m.nsfId ? String(m.nsfId) : '',
        fideId: m.fideId ? String(m.fideId) : '',
        givenName: m.givenName || '',
        lastName: m.lastName || '',
        name: [m.givenName, m.lastName].filter(Boolean).join(' '),
        club: m.club || '',
        birthYear: m.birthYear ? String(m.birthYear) : '',
        fed: m.fed || '',
        title: m.title || '',
        standardRating: 0,
        rapidRating: 0,
        blitzRating: 0,
        kFactors: m.kFactors || null,
        membershipYear: m.membershipYear || null,
        fideLoaded: !m.fideId
    };
}

function withFideRecord(member, fide) {
    if (!fide) return member;
    return {
        ...member,
        fed: fide.fed || member.fed,
        title: fide.title || member.title,
        standardRating: fide.standardRating || 0,
        rapidRating: fide.rapidRating || 0,
        blitzRating: fide.blitzRating || 0,
        birthYear: member.birthYear || (fide.birthYear ? String(fide.birthYear) : ''),
        fideLoaded: true
    };
}

// FIDE's row for one FIDE ID, from the same endpoint the worker searches, or null
// when FIDE's list has no such ID; throws when there is no answer to be had. `?id=`
// finds the one ID (functions/lib/fide-list.js). It used to be `?q=` with the ID,
// a search through the whole list, and picking such a member stalled for a second.
async function fetchFideRow(id) {
    if (!/^\d{4,12}$/.test(id)) return null;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    const ask = param => fetch(`${FUNCTIONS_BASE}/searchFide?${param}=${encodeURIComponent(id)}`, { signal: controller.signal });
    try {
        let res = await ask('id');
        // A searchFide deployed before `?id=` refuses it: asked the old way.
        if (res.status === 400) res = await ask('q');
        if (!res.ok) throw new Error(`searchFide answered ${res.status}`);
        const data = await res.json();
        return (Array.isArray(data.results) ? data.results : []).find(r => String(r.fideId) === id) || null;
    } finally {
        clearTimeout(timer);
    }
}

// The FIDE rows asked for in this page's life, by FIDE ID: { promise, done, record }.
// A member's row is asked for once, as soon as a search shows them without it (drawRun),
// and their pick takes it from here. A failure is forgotten, to be asked again.
const fideRows = new Map();
const FIDE_ROWS_KEPT = 300;

function fideRow(fideId) {
    const id = String(fideId || '').trim();
    let row = fideRows.get(id);
    if (row) return row;
    row = { done: false, record: null };
    row.promise = fetchFideRow(id)
        .then(record => {
            row.record = record;
            row.done = true;
            return record;
        })
        .catch(() => {
            if (fideRows.get(id) === row) fideRows.delete(id);
            return null;
        });
    if (fideRows.size >= FIDE_ROWS_KEPT) fideRows.delete(fideRows.keys().next().value);
    fideRows.set(id, row);
    return row;
}

// --- Search sources ---------------------------------------------------------
//
// Two boxes, two rules:
//
//  - Players tab (the box that adds someone straight to the tournament): when
//    the active group is bound to one of the arbiter's own databases, that
//    database IS the roster they are picking from, so it answers the box by
//    default. A switch at the end of the box flips it to FIDE for the odd
//    player who is not in the database yet. A group with no database of its own
//    only has FIDE, and no switch.
//  - Add Player modal: the same default, but with a switch, because this is the
//    dialog you reach for when the person you want is NOT in your database yet —
//    which is exactly when you want to pull them out of FIDE.
//
// The switch is therefore only shown when there is something to switch between,
// i.e. when a database is bound at all.

let searchSource = 'db';
// The Players-tab box's own choice. Back to 'db' whenever the bound database
// changes, so the default always follows the binding.
let globalSource = 'db';
// The database's players, mirrored from IndexedDB. Loaded when the bound
// database changes rather than per keystroke — matching is local and instant
// from then on.
let dbPlayerCache = [];
let dbPlayerCacheDbId = '';

// Whether the group's own database, rather than FIDE, is what a search should
// look in by default. False for every tournament that predates player databases.
function boundDatabaseId() {
    return getActiveSettings().ratingDbId || '';
}

// Load (and keep fresh) the bound database's players. Safe to call often — the
// work only happens when the bound database actually changes.
function ensureDbPlayerCache() {
    const dbId = boundDatabaseId();
    if (!dbId) {
        dbPlayerCacheDbId = '';
        dbPlayerCache = [];
        return;
    }
    if (dbPlayerCacheDbId === dbId) return;
    dbPlayerCacheDbId = dbId;
    globalSource = 'db';
    dbPlayerCache = [];
    getCachedPlayers(dbId).then(players => { dbPlayerCache = players; });
    syncPlayers(dbId).then(players => { dbPlayerCache = players; }).catch(() => {});
}

// Keep the Players-tab search box honest about where it is searching, and warm
// the cache behind it. Called whenever the active tournament/group may have
// changed (js/app.js updateUIState).
export function syncPlayerSearchSources() {
    ensureDbPlayerCache();

    const useDb = globalSearchUsesDatabase();
    const nsf = nsfSearchAvailable();
    if (globalSearchSource) globalSearchSource.style.display = dbPlayerCacheDbId ? 'flex' : 'none';
    if (globalSearchUseDb) globalSearchUseDb.checked = useDb;
    globalSearchSource?.querySelectorAll('[data-side]').forEach(el => {
        el.classList.toggle('active', (el.getAttribute('data-side') === 'db') === useDb);
        if (el.getAttribute('data-side') === 'fide') el.textContent = nsf ? 'NSF / FIDE' : 'FIDE';
    });
    if (globalPlayerSearch) {
        globalPlayerSearch.placeholder = useDb
            ? tr('Search my database to add player...')
            : (nsf ? tr('Search NSF and FIDE to add player...') : tr('Search FIDE Database to add player...'));
    }
}

function globalSearchUsesDatabase() {
    return !!boundDatabaseId() && globalSource === 'db';
}

export function syncPlayerSearchSource() {
    const toggle = document.getElementById('player-search-source');
    if (!toggle) return;

    const dbId = boundDatabaseId();
    toggle.style.display = dbId ? 'flex' : 'none';
    ensureDbPlayerCache();

    // No database bound: FIDE is the only source, and the switch is hidden.
    setSearchSource(dbId ? 'db' : 'fide');
}

function setSearchSource(source) {
    searchSource = source;
    const nsf = nsfSearchAvailable();
    document.querySelectorAll('[data-search-source]').forEach(btn => {
        btn.classList.toggle('active', btn.getAttribute('data-search-source') === source);
        if (btn.getAttribute('data-search-source') === 'fide') btn.textContent = nsf ? 'NSF / FIDE' : 'FIDE';
    });
    const label = document.getElementById('player-db-search-label');
    if (label) {
        label.textContent = source === 'db' ? tr('Search my database')
            : (nsf ? tr('Search NSF and FIDE') : tr('Search FIDE Database'));
    }
    if (playerDbSearch) {
        playerDbSearch.placeholder = (source !== 'db' && nsf)
            ? tr('Search by name, NSF ID or FIDE ID...')
            : tr('Search by name or FIDE ID...');
    }
    if (dbSearchResults) dbSearchResults.classList.add('hidden');
    if (playerDbSearch) playerDbSearch.value = '';
}

// A database player rendered in the same shape the FIDE results use, so one
// renderer and one click handler serve both sources. The rating shown is the one
// from the list this group is bound to.
function dbPlayerToResult(p, listId) {
    const entry = (p.ratings || {})[listId];
    const value = entry && typeof entry === 'object' ? (entry.value || 0) : (parseInt(entry, 10) || 0);
    return {
        dbPlayerId: p.id,
        fideId: p.fideId || '',
        name: p.name || '',
        lastName: p.lastName || '',
        givenName: p.givenName || '',
        fed: p.fed || '',
        title: p.title || '',
        club: p.club || '',
        birthYear: p.birthYear || '',
        birthDate: p.birthDate || '',
        nsfId: p.nsfId || '',
        // Fed into getPlayerDisplayRating via the ratings map, so a player with no
        // rating in this list still falls back exactly as everywhere else.
        ratings: p.ratings || {},
        rating: value
    };
}

function searchOwnDatabase(query) {
    const settings = getActiveSettings();
    const results = searchCachedPlayers(dbPlayerCache, query, 15);
    return results.map(p => dbPlayerToResult(p, settings.ratingListId));
}

function setupSearch(inputEl, resultsEl, spinnerEl, isGlobal) {
    if (!inputEl) return;

    inputEl.addEventListener('focus', prewarmFideSearch);

    let debounceTimeout;
    inputEl.addEventListener('input', (e) => {
        const query = e.target.value.trim();

        clearTimeout(debounceTimeout);
        if (query.length < 3) {
            // Whatever is still on its way for the longer query is no longer wanted.
            runs.delete(isGlobal);
            resultsEl.classList.add('hidden');
            spinnerEl.classList.add('hidden');
            return;
        }

        // The own-database search is local, so it answers immediately — no
        // debounce, no spinner, no worker round trip.
        const useOwnDatabase = isGlobal
            ? globalSearchUsesDatabase()
            : searchSource === 'db';
        if (useOwnDatabase) {
            runs.delete(isGlobal);
            spinnerEl.classList.add('hidden');
            renderResultList(resultsEl, [{ items: searchOwnDatabase(query) }], isGlobal, '');
            return;
        }

        spinnerEl.classList.remove('hidden');
        debounceTimeout = setTimeout(() => startSearch(query, resultsEl, spinnerEl, isGlobal), 300);
    });

    document.addEventListener('click', (e) => {
        if (inputEl && !inputEl.contains(e.target) && !resultsEl.contains(e.target)) {
            resultsEl.classList.add('hidden');
        }
    });
}

// `sections` is [{ heading?, items }], drawn in order; `errorText` is a FIDE failure
// to show under whatever did come back.
function renderResultList(resultsEl, sections, isGlobal, errorText) {
    if (!resultsEl) return;
    const errorLine = errorText
        ? `<div class="search-result-item" style="color: var(--danger); cursor: default;"><i class="ri-error-warning-line"></i> ${escapeHtml(errorText)}</div>`
        : '';

    if (sections.every(s => s.items.length === 0)) {
        resultsEl.innerHTML = errorLine || `<div class="search-result-item">${tr('No players found')}</div>`;
        resultsEl.classList.remove('hidden');
        return;
    }

    resultsEl.innerHTML = '';
    sections.forEach(section => {
        if (section.items.length === 0) return;
        if (section.heading) {
            const heading = document.createElement('div');
            heading.className = 'search-result-group';
            heading.textContent = section.heading;
            resultsEl.appendChild(heading);
        }
        section.items.forEach(player => resultsEl.appendChild(resultItem(player, resultsEl, isGlobal)));
    });
    if (errorLine) resultsEl.insertAdjacentHTML('beforeend', errorLine);
    resultsEl.classList.remove('hidden');
}

function fideProfileLink(fideId) {
    return `<a href="https://ratings.fide.com/profile/${encodeURIComponent(fideId)}" target="_blank" rel="noopener" style="color: var(--primary); text-decoration: none;">${escapeHtml(fideId)}</a>`;
}

function resultItem(result, resultsEl, isGlobal) {
    // A member FIDE's answer left out shows their own row once it is in, and is picked with it.
    const row = result.source === 'nsf' && !result.fideLoaded ? fideRows.get(result.fideId) : null;
    let player = row && row.done ? withFideRecord(result, row.record) : result;
    const { display, tooltip } = getPlayerDisplayRating(player);
    const item = document.createElement('div');
    item.className = 'search-result-item';
    if (player.source === 'nsf') {
        // Club and year of birth tell namesakes apart. No rating until FIDE's row is in.
        item.innerHTML = `
            <div class="search-result-header">
                <span class="search-result-name">${escapeHtml(player.name)}</span>
                ${player.fideLoaded ? `<span class="search-result-rating" title="${escapeHtml(tooltip)}">${escapeHtml(display)}</span>` : ''}
            </div>
            <div class="search-result-details">
                ${player.club ? `<span><i class="ri-community-line"></i> ${escapeHtml(player.club)}</span>` : ''}
                ${player.birthYear ? `<span>${escapeHtml(tr('b. {year}', { year: player.birthYear }))}</span>` : ''}
                ${player.title ? `<span><i class="ri-medal-line"></i> ${escapeHtml(player.title)}</span>` : ''}
                ${player.fideId ? `<span>FIDE: ${fideProfileLink(player.fideId)}</span>` : ''}
            </div>
        `;
    } else {
        item.innerHTML = `
            <div class="search-result-header">
                <span class="search-result-name">${escapeHtml(player.name)}</span>
                <span class="search-result-rating" title="${escapeHtml(tooltip)}">${escapeHtml(display)}</span>
            </div>
            <div class="search-result-details">
                <span><i class="ri-flag-line"></i> ${escapeHtml(player.fed)}</span>
                <span><i class="ri-medal-line"></i> ${escapeHtml(player.title || tr('No Title'))}</span>
                ${player.fideId
                    ? `<span>ID: ${fideProfileLink(player.fideId)}</span>`
                    : (player.club ? `<span><i class="ri-community-line"></i> ${escapeHtml(player.club)}</span>` : '')}
            </div>
        `;
    }
    if (row && !row.done) {
        row.promise.then(record => {
            if (!record || !item.isConnected) return;
            player = withFideRecord(player, record);
            const shown = getPlayerDisplayRating(player);
            item.querySelector('.search-result-header')?.insertAdjacentHTML('beforeend',
                `<span class="search-result-rating" title="${escapeHtml(shown.tooltip)}">${escapeHtml(shown.display)}</span>`);
        });
    }

    // One pick at a time: an NSF pick may wait on FIDE, and a second click meanwhile
    // would add the player twice.
    let picking = false;
    item.addEventListener('click', async (e) => {
        // The FIDE profile link opens the profile; it does not pick the player.
        if (e.target.closest('a') || picking) return;
        picking = true;
        let picked = player;
        try {
            if (!await checkActiveTournament()) return;
            // A member whose FIDE row is not in yet: waited for, so they arrive with
            // their ratings and title like any FIDE pick. Usually it came in while the
            // list was read.
            if (picked.source === 'nsf' && !picked.fideLoaded) {
                const pending = fideRow(picked.fideId);
                if (!pending.done) item.style.opacity = '0.6';
                picked = withFideRecord(picked, await pending.promise);
            }
        } finally {
            picking = false;
            item.style.opacity = '';
        }
        if (picked.source === 'nsf') {
            // NSF's K for the FIDE list this group is rated on; none in a youth group.
            picked = { ...picked, kFactor: nsfKFactor(picked, getActiveSettings()) };
            // Their badge shows the membership the register just showed, not "not checked yet".
            rememberNsfPick(picked);
        }

        const playerWithActiveRating = {
            ...picked,
            rating: getPlayerDisplayRating(picked).display
        };

        if (isGlobal) {
            addPlayerFromSearch(playerWithActiveRating);
        } else {
            fillPlayerForm(playerWithActiveRating);
        }
        resultsEl.classList.add('hidden');
        const inputEl = isGlobal ? globalPlayerSearch : playerDbSearch;
        if (inputEl) inputEl.value = '';
    });
    return item;
}

// Picking a result in the Players-tab box adds that player to the list at once.
// It still goes through the Add Player form — filled and submitted without being
// shown — so a player added this way is built by exactly the same code, with the
// same defaults, as one typed in by hand.
async function addPlayerFromSearch(player) {
    // The modal used to be the moment to notice a double click; without it, ask.
    const already = state.players.find(p =>
        (player.fideId && String(p.fideId) === String(player.fideId)) ||
        (player.nsfId && String(p.nsfId) === String(player.nsfId)) ||
        (player.dbPlayerId && String(p.dbPlayerId) === String(player.dbPlayerId)));
    if (already) {
        const name = [already.givenName, already.lastName].filter(Boolean).join(' ') || player.name;
        const ok = await window.Dialog.confirm(
            tr('{name} is already on the list. Add them again?', { name }),
            tr('Already Added'),
            tr('Add Anyway')
        );
        if (!ok) return;
    }

    state.editingPlayerId = null;
    addPlayerModal.querySelector('h2').textContent = tr('Register New Player');
    if (btnSavePlayer) btnSavePlayer.textContent = tr('Add Player');
    addPlayerForm.reset();
    fillPlayerForm(player);
    addPlayerForm.requestSubmit();
}

function fillPlayerForm(player) {
    // Carried through the form so the saved tournament player keeps its link to
    // the database record (blank for a FIDE result, which clears any stale id
    // left over from a previous pick).
    const dbIdField = document.getElementById('player-db-player-id');
    if (dbIdField) dbIdField.value = player.dbPlayerId || '';

    if (document.getElementById('player-title')) document.getElementById('player-title').value = player.title || '';
    if (document.getElementById('player-given-name')) document.getElementById('player-given-name').value = player.givenName || '';
    // `name` stands in only for a result with no name parts at all. A database
    // player saved with a given name alone has name === givenName, and copying it
    // into the last name too entered them as "Ola Ola".
    if (document.getElementById('player-last-name')) {
        document.getElementById('player-last-name').value = player.lastName || (player.givenName ? '' : (player.name || ''));
    }
    if (document.getElementById('player-fide-id')) document.getElementById('player-fide-id').value = player.fideId;
    // From an NSF result or a database player that has one; blank otherwise, like the
    // database id above.
    if (document.getElementById('player-nsf-id')) document.getElementById('player-nsf-id').value = player.nsfId || '';
    if (document.getElementById('player-rating')) document.getElementById('player-rating').value = player.rating;
    if (document.getElementById('player-club')) document.getElementById('player-club').value = player.club || '';
    if (document.getElementById('player-fed')) document.getElementById('player-fed').value = player.fed || '';

    if (document.getElementById('player-std-rating')) document.getElementById('player-std-rating').value = player.standardRating || 0;
    if (document.getElementById('player-rapid-rating')) document.getElementById('player-rapid-rating').value = player.rapidRating || 0;
    if (document.getElementById('player-blitz-rating')) document.getElementById('player-blitz-rating').value = player.blitzRating || 0;
    // NSF's K for an NSF pick; blank — the rating engine's default — for anyone else, so
    // a K from an earlier pick never stays behind on the next player.
    if (document.getElementById('player-k-factor')) document.getElementById('player-k-factor').value = player.kFactor || '';

    if (document.getElementById('player-birthyear')) {
        document.getElementById('player-birthyear').value = player.birthYear || '';
    }
    if (document.getElementById('player-birthdate')) {
        document.getElementById('player-birthdate').value = player.birthDate || '';
    }

    // Add a small visual feedback
    const form = document.getElementById('add-player-form');
    if (form) {
        form.classList.add('pulse-highlight');
        setTimeout(() => form.classList.remove('pulse-highlight'), 1000);
    }
}

// Wires both search boxes. Call once from app.js during startup.
export function initPlayerSearch({ checkActiveTournament: guard } = {}) {
    if (typeof guard === 'function') checkActiveTournament = guard;
    setupSearch(playerDbSearch, dbSearchResults, searchSpinner, false);
    setupSearch(globalPlayerSearch, globalSearchResults, globalSearchSpinner, true);
    syncPlayerSearchSources();

    document.querySelectorAll('[data-search-source]').forEach(btn => {
        btn.addEventListener('click', () => setSearchSource(btn.getAttribute('data-search-source')));
    });

    // Flipping the Players-tab switch re-runs whatever is already typed against
    // the other source, rather than making the arbiter type it again.
    globalSearchUseDb?.addEventListener('change', () => {
        globalSource = globalSearchUseDb.checked ? 'db' : 'fide';
        syncPlayerSearchSources();
        globalSearchResults?.classList.add('hidden');
        globalPlayerSearch?.dispatchEvent(new Event('input'));
        globalPlayerSearch?.focus();
    });

    // The bound database can change between openings (a different tournament, or
    // the binding edited), so the source switch is re-evaluated every time the
    // modal is shown rather than once at startup.
    if (addPlayerModal) {
        new MutationObserver(() => {
            if (addPlayerModal.classList.contains('active')) syncPlayerSearchSource();
        }).observe(addPlayerModal, { attributes: true, attributeFilter: ['class'] });
    }
}
